/**
 * Automatic Bonus Progression (GM Core; Foundry: actor/character/automatic-bonus-progression.ts).
 * The Level tables of the Variant Rule (#12): attack, defense, Perception and save potency,
 * devastating attacks (striking dice), the skill potency steps and the attribute apex. The engine
 * turns them into potency modifiers and strips the runes they replace; the guide opens the skill
 * potency and apex Choices. Foundry's "fundamental potency" flavour is the one implemented: a
 * potency synthetic on strikes, property runes kept in the slots the attack potency gives.
 */
import type { LevelNumber } from "../character/model";
import { createModifier } from "./modifiers";
import type { EngineState } from "./state";

export type ZeroToThree = 0 | 1 | 2 | 3;

export function abpAttackPotency(level: number): ZeroToThree {
  return level < 2 ? 0 : level < 10 ? 1 : level < 16 ? 2 : 3;
}

export function abpDefensePotency(level: number): ZeroToThree {
  return level < 5 ? 0 : level < 11 ? 1 : level < 18 ? 2 : 3;
}

export function abpPerceptionPotency(level: number): ZeroToThree {
  return level < 7 ? 0 : level < 13 ? 1 : level < 19 ? 2 : 3;
}

export function abpSavePotency(level: number): ZeroToThree {
  return level < 8 ? 0 : level < 14 ? 1 : level < 20 ? 2 : 3;
}

/** Devastating attacks: the striking dice a weapon or unarmed Strike gains beyond its first. */
export function abpStrikingDice(level: number): ZeroToThree {
  return level < 4 ? 0 : level < 12 ? 1 : level < 19 ? 2 : 3;
}

/** The Level the attribute apex arrives at. */
export const ABP_APEX_LEVEL: LevelNumber = 17;

/**
 * One skill potency step: a new +1 to a skill without potency, or a raise of a skill at `from`
 * by one. GM Core's totals (3rd: one at +1; 6th: two at +1; 9th: one at +2, one at +1; 13th:
 * two at +2, one at +1; 15th: three at +2, one at +1; 20th: two at +3, two at +2, two at +1)
 * read as these increments; each step is a Choice answered with a skill.
 */
export type SkillPotencyStep = { kind: "new" } | { kind: "raise"; from: 1 | 2 };

export const SKILL_POTENCY_STEPS: Readonly<Partial<Record<LevelNumber, readonly SkillPotencyStep[]>>> = Object.freeze({
  3: [{ kind: "new" }],
  6: [{ kind: "new" }],
  9: [{ kind: "raise", from: 1 }],
  13: [{ kind: "raise", from: 1 }, { kind: "new" }],
  15: [{ kind: "raise", from: 1 }, { kind: "new" }],
  20: [{ kind: "raise", from: 2 }, { kind: "raise", from: 2 }, { kind: "raise", from: 1 }, { kind: "new" }, { kind: "new" }],
});

/** The skill potency step a Choice at a Level and ordinal stands for, if the table has one. */
export function skillPotencyStep(level: number, ordinal: number): SkillPotencyStep | undefined {
  return SKILL_POTENCY_STEPS[level as LevelNumber]?.[ordinal];
}

/** A skill potency Choice's answer as the guide and the engine read it. */
export interface SkillPotencyAnswer {
  level: LevelNumber;
  ordinal: number;
  skill: string;
}

/**
 * Fold skill potency answers into potencies by skill, in Level and ordinal order. An answer that
 * does not fit its step (a raise of a skill not at `from`, a new potency on a skill that has one)
 * is skipped, as the guide reopens it; `stopBefore` leaves out the answers from that point on so
 * candidates can be judged before a Choice.
 */
export function skillPotencies(answers: readonly SkillPotencyAnswer[], stopBefore?: { level: LevelNumber; ordinal: number }): Map<string, number> {
  const out = new Map<string, number>();
  const sorted = [...answers].sort((a, b) => a.level - b.level || a.ordinal - b.ordinal);
  for (const answer of sorted) {
    if (stopBefore && (answer.level > stopBefore.level || (answer.level === stopBefore.level && answer.ordinal >= stopBefore.ordinal))) break;
    const step = skillPotencyStep(answer.level, answer.ordinal);
    if (!step || !answer.skill) continue;
    const current = out.get(answer.skill) ?? 0;
    if (step.kind === "new" && current === 0) out.set(answer.skill, 1);
    else if (step.kind === "raise" && current === step.from) out.set(answer.skill, current + 1);
  }
  return out;
}

/**
 * Foundry's `AutomaticBonusProgression.concatModifiers`, plus the skill potencies the Choices
 * assigned: potency-type modifiers on saves, AC, Perception and the chosen skills, and the attack
 * potency as a strike synthetic (the fundamental potency flavour). Runs before statistics derive.
 */
export function applyAutomaticBonusProgression(state: EngineState, potencies: ReadonlyMap<string, number>): void {
  const { level, synthetics } = state;
  const push = (selector: string, slug: string, label: string, value: number): void => {
    if (value <= 0) return;
    (synthetics.modifiers[selector] ??= []).push(() => createModifier({ slug, label, value, type: "potency" }));
  };
  push("saving-throw", "save-potency", "Save Potency", abpSavePotency(level));
  push("ac", "defense-potency", "Defense Potency", abpDefensePotency(level));
  push("perception", "perception-potency", "Perception Potency", abpPerceptionPotency(level));
  const attack = abpAttackPotency(level);
  if (attack > 0) (synthetics.weaponPotency["strike-attack-roll"] ??= []).push({ label: "Attack Potency", type: "potency", bonus: attack, predicate: [] });
  for (const [skill, value] of potencies) push(skill, "skill-potency", "Skill Potency", value);
}
